import React, { useCallback, useState } from "react";
import { Table, Button } from "react-bootstrap";
import useSWR from "swr";
import { Link, useNavigate } from "react-router-dom";
import { serverUrl, fetcher, swrConfig } from "../helpers/utility";
import variables from "../styles/utils.module.scss";
import Loading from "./loading";
import TabWithFilter from "./TabWithFilter";
import { useForm } from "react-hook-form";
import { ArrowRightIcon } from "lucide-react";
import { APPROVED, IN_REVIEW } from "../helpers/constants";

export default function LocationsContent({ summaryOnly = false }) {
  const steps = { ACTIVE: "ACTIVE", IN_REVIEW: "IN_REVIEW" };
  const [step, setStep] = useState(steps.ACTIVE);
  const navigate = useNavigate();
  const { register, watch } = useForm();
  const formValues = watch();
  const {
    data: ownLocationData,
    error: locationError,
    isLoading: locationIsLoading,
  } = useSWR(`${serverUrl}/location/own`, fetcher, swrConfig);

  const activeLocations = useCallback(() => {
    if (ownLocationData && ownLocationData.locations?.length > 0) {
      let approvedLocations = ownLocationData.locations?.filter((location) => {
        return (
          location.status === APPROVED &&
          (!formValues?.locationName ||
            location?.name?.toLowerCase().includes(formValues?.locationName?.toLowerCase()) ||
            formValues?.locationName?.length < 3)
        );
      });

      if (summaryOnly) return approvedLocations.slice(0, 5);
      else return approvedLocations;
    }
  }, [ownLocationData, formValues?.locationName]);

  const pendingLocations = useCallback(() => {
    if (ownLocationData && ownLocationData.locations?.length > 0) {
      let unapprovedLocations = ownLocationData.locations?.filter((location) => {
        return (
          location.status === IN_REVIEW &&
          (!formValues?.locationName ||
            location?.name?.toLowerCase().includes(formValues?.locationName?.toLowerCase()) ||
            formValues?.locationName?.length < 3)
        );
      });

      return unapprovedLocations;
    } else {
      return [];
    }
  }, [ownLocationData, formValues?.locationName]);

  const goToLocation = useCallback(
    ({ locationId }) => {
      navigate(`/add-location?locationId=${locationId}`);
    },
    [navigate]
  );

  const goToLocationSubscription = useCallback(
    ({ locationId }) => {
      navigate(`/add-location?locationId=${locationId}&initialStep=PAYMENT`);
    },
    [navigate]
  );

  const goToPlacementDetails = useCallback(
    ({ locationId }) => {
      navigate(`/location?locationId=${locationId}`);
    },
    [navigate]
  );


  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <div className="d-flex flex-row mb-2">
        <h2 className="h3">Locations</h2>
        {summaryOnly ? (
          <p className="text-muted medium ms-auto">
            {activeLocations()?.length} of {activeLocations()?.length} live locations
          </p>
        ) : null}
      </div>
      <div className="space-y-4">
        <TabWithFilter
          tabs={[
            {
              label: "Active",
              onClick: () => {
                console.log(`Active`);
                setStep(steps.ACTIVE);
              },
              active: step === steps.ACTIVE,
            },
            {
              label: "In Review",
              onClick: () => {
                console.log(`In Review`);
                setStep(steps.IN_REVIEW);
              },
              active: step === steps.IN_REVIEW,
            },
          ]}
          filterInputId={summaryOnly ? null : "locationName"}
          filterRegister={register}
          filterPlaceholder="Search by location name"
        />

        {locationIsLoading ? <Loading inline={true} inlineHeight={300} /> : null}

        {step === steps.ACTIVE ? (
          <>
            {!locationIsLoading && activeLocations()?.length > 0 ? (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Location</th>
                    <th className="d-none d-md-table-cell">Capacity</th>
                    <th className="d-none d-md-table-cell">Contact</th>
                    <th style={{ width: "75px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeLocations().map((location) => {
                    return (
                      <tr
                        key={location._id}
                        onClick={() => {
                          goToLocation({ locationId: location._id });
                        }}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        <td>
                          {location.name}
                          {!location.subscriptionActive ? (
                            <i
                              className={`bi bi-credit-card`}
                              onClick={(event) => {
                                goToLocationSubscription({ locationId: location._id });
                                event.stopPropagation();
                              }}
                              style={{
                                fontSize: "14px",
                                marginLeft: "6px",
                                cursor: "pointer",
                              }}
                              title={`Subscription missing, will not appear in search results.`}
                            ></i>
                          ) : null}
                        </td>
                        <td className="d-none d-md-table-cell">
                          {location.capacityUsed}/{location.capacityMax}
                        </td>
                        <td className="d-none d-md-table-cell">{location.email}</td>
                        <td style={{ textAlign: "left" }}>
                          <i
                            className={`bi bi-pencil`}
                            style={{
                              fontSize: "14px",
                              color: variables.green,
                              padding: "3px",
                              marginRight: "6px",
                              cursor: "pointer",
                            }}
                            onClick={(event) => {
                              goToLocation({ locationId: location._id });
                              event.stopPropagation();
                            }}
                          ></i>

                          <i
                            className={`bi bi-card-text`}
                            style={{
                              fontSize: "16px",
                              color: variables.green,
                              padding: "3px",
                              cursor: "pointer",
                            }}
                            onClick={(event) => {
                              goToPlacementDetails({ locationId: location._id });
                              event.stopPropagation();
                            }}
                          ></i>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : null}

            {!locationIsLoading && activeLocations()?.length === 0 ? (
              <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ marginTop: 20, marginBottom: 10 }}
              >
                Welcome! Get started by adding your first location.
              </div>
            ) : null}

            <div
              style={{
                marginTop: 0,
                // Important for mobile
                marginBottom: 15,
              }}
            >
              <Button
                onClick={() => {
                  navigate("/add-location");
                }}
                variant="primary"
              >
                ADD LOCATION
              </Button>
            </div>
          </>
        ) : null}

        {step === steps.IN_REVIEW ? (
          <>
            {/* PENDING PLACEMENT */}
            {pendingLocations()?.length > 0 ? (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingLocations().map((location) => {
                    return (
                      <tr
                        key={location._id}
                        onClick={() => {
                          goToLocation({ locationId: location._id });
                        }}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        <td>{location.name}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : null}

            {pendingLocations()?.length === 0 ? (
              <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ marginBottom: 10 }}
              >
                No results found.
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
